import dayjs from "dayjs";
import { IGeneralInfo, IGeneralInfoEntryAdditionalRetention } from "../models/generalInfo";
import { IReportEntryDay } from "../models/report";
import { IPayrollEntryDayAdditionalRetention } from "../models/payroll";

export const getDayWorkedHours = (day: IReportEntryDay): number => {
    let total = 0;
    const dayDate = dayjs(day.entry_date);
    const dayDay = dayDate.day();

    if (dayDay === 0 || day.is_holiday) {
        return 0;
    }

    total = calculateWorkedHoursPerDay(day);

    if (dayDay === 6) {
        const doubleHours = getDayDoubleHours(day);
        if (total >= doubleHours) {
            total -= doubleHours;
        }
    }

    return total;
}

const calculateWorkedHoursPerDay = (day: IReportEntryDay): number => {
    let total = 0;

    for (let i = 0; i < day.report_entry_day_events.length; i++) {
        const event = day.report_entry_day_events[i];
        if (!event.start_time || !event.end_time) continue;
        const startTime = dayjs(event.start_time).set('seconds', 0);
        const endTime = dayjs(event.end_time).set('seconds', 0);
        if (endTime > startTime) {
            total += endTime.diff(startTime, 'hours', true);
        }
    }

    return total;
}

export const getDayExtraHours = (day: IReportEntryDay): number => {
    let total = 0;
    const dayDate = dayjs(day.entry_date);
    const dayDay = dayDate.day();

    // Monday to Friday
    if (dayDay > 0 && dayDay < 6 && !day.is_holiday) {
        const dayHours = getDayWorkedHours(day);
        if (dayHours >= 9) {
            total = dayHours - 9;
        }
        const nightHours = getDayNightHours(day);
        total = Math.max(0, total - nightHours);
    }

    return total;
}

export const getDayDoubleHours = (day: IReportEntryDay): number => {
    let total = 0;
    const dayDate = dayjs(day.entry_date);
    const dayDay = dayDate.day();

    // Sunday
    if (dayDay === 0 || day.is_holiday) {
        total = calculateWorkedHoursPerDay(day);
    }
    // Saturday
    else if (dayDay === 6) {
        const midday = dayjs(day.entry_date).set('hour', 12).set('minute', 0).set('second', 0);

        for (let i = 0; i < day.report_entry_day_events.length; i++) {
            const event = day.report_entry_day_events[i];
            if (!event.start_time || !event.end_time) continue;
            const startDate = dayjs(event.start_time).set('second', 0);
            const endDate = dayjs(event.end_time).set('second', 0);

            // If we start in the morning and finish past midday
            if (startDate.isBefore(midday) && endDate.isAfter(midday)) {
                total += endDate.diff(midday, 'hours', true);
            }
            // If we work past midday
            else if (startDate.isAfter(midday) && endDate.isAfter(midday)) {
                total += endDate.diff(startDate, 'hours', true);
            }
        }
    }

    return total;
}

export const getDayNightHours = (day: IReportEntryDay): number => {
    let total = 0;

    for (let k = 0; k < day.report_entry_day_events.length; k++) {
        const event = day.report_entry_day_events[k];
        if (!event.start_time || !event.end_time) continue;

        // 9:00 PM
        const nineNight = dayjs(event.end_time).set('hour', 21).set('minute', 0).set('second', 0).set('millisecond', 0);
        const startTime = dayjs(event.start_time);
        const endTime = dayjs(event.end_time);

        if (startTime > nineNight) {
            total += endTime.diff(startTime, "hours", true);
        } else if (endTime > nineNight) {
            total += endTime.diff(nineNight, "hours", true);
        }
    }

    if (total < 0) {
        total = 0;
    }

    return total;
}

const getTotalDeductionsExcludingLoans = (infos: IGeneralInfo[], infoId: number, employeeId: number): number => {
    let total = 0;
    const info = infos.find(i => i.id === infoId);
    if (info) {
        const entry = info.general_info_entries.find(e => e.employee_id === employeeId);
        if (entry) {
            const additionalRetentions = entry.general_info_entry_additional_retentions
                .filter(r => r.is_active);
            // Per Capita Retentions
            total += additionalRetentions.filter(r => /\b(?:retencion\s+)?per\s+capita(?:\s+adicional)?\b/
                .test(r.description.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "")))
                .reduce((acc, r) => acc + r.amount, 0) / 4.33;
        }
    }
    return total;
}

// A retention counts as a "loan" if its description (case-insensitive,
// accent-stripped) contains "cuota" or "prestamo". Used by the payroll
// dropdown and the deduction calculation so they always agree on what
// is a loan.
const isLoanDescription = (description: string): boolean => {
    const normalized = description.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    return normalized.includes("cuota") || normalized.includes("prestamo")
}

const getTotalDeductionsFromLoans = (infos: IGeneralInfo[], infoId: number, employeeId: number, loans: IPayrollEntryDayAdditionalRetention[]): number => {
    let total = 0;
    const info = infos.find(i => i.id === infoId);
    if (info) {
        const entry = info.general_info_entries.find(e => e.employee_id === employeeId);
        if (entry) {
            const additionalRetentions = entry.general_info_entry_additional_retentions;
            // GI is_active controls auto-add and dropdown labeling, not the
            // calculation itself. The day's per-row is_active is the final
            // say: if a loan is active on the day, it's deducted, even if
            // the GI source is currently marked inactive.
            const loanFees = additionalRetentions.filter(r => isLoanDescription(r.description))
            const activeLoans = loans.filter(l => l.is_active);
            for (const loan of activeLoans) {
                const loanFee = loanFees.find(r => r.id === loan.general_info_entry_additional_retention_id);
                if (loanFee) {
                    total += loanFee.amount;
                }
            }
        }
    }

    return total;
}

const getEmployeeLoanRetentions = (employeeId: number | null, infoId: number | null, infos: IGeneralInfo[] | null): IGeneralInfoEntryAdditionalRetention[] => {
    if (infoId !== null && employeeId !== null) {
        const info = infos?.find(i => i.id === infoId);
        if (info) {
            const entry = info.general_info_entries.find(e => e.employee_id === employeeId);
            if (entry) {
                return entry.general_info_entry_additional_retentions
                    .filter(r => isLoanDescription(r.description))
                    .slice()
                    .sort((a, b) => a.position - b.position)
            }
        }
    }

    return [];
}

const getEmployeeActiveLoanRetentions = (employeeId: number | null, infoId: number | null, infos: IGeneralInfo[] | null): IGeneralInfoEntryAdditionalRetention[] => {
    return getEmployeeLoanRetentions(employeeId, infoId, infos).filter(r => r.is_active);
}

export default {
    getTotalDeductionsExcludingLoans,
    getEmployeeLoanRetentions,
    getEmployeeActiveLoanRetentions,
    getTotalDeductionsFromLoans
}
